import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { IkoCertEditor } from '@/components/IkoCertEditor';
import { ikoCertName, MONTH_KEYS } from '@/lib/iko';
import {
  Award, ShieldCheck, Pencil, Loader2, MapPin, Calendar, CreditCard, ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';

export const IkoCard = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certified, setCertified] = useState(user?.iko_certified || false);
  const [certs, setCerts] = useState(user?.iko_certifications || []);

  const startEdit = () => {
    setCertified(user?.iko_certified || false);
    setCerts(user?.iko_certifications?.length ? user.iko_certifications : []);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/iko', {
        iko_certified: certified,
        iko_certifications: certified ? certs.filter((c) => c.cert) : [],
      });
      await refreshUser();
      toast.success(t('iko.savedToast'));
      setEditing(false);
    } catch (e) {
      toast.error(apiError(e, t('iko.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const when = (c) => {
    if (!c.year) return null;
    const m = c.month ? t(`iko.months.${MONTH_KEYS[c.month - 1]}`) : '';
    return `${m ? m + ' ' : ''}${c.year}`;
  };

  const list = user?.iko_certifications || [];

  return (
    <div className="kite-card p-6 mt-6" data-testid="profile-iko-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Award className="w-5 h-5 text-[#48CAE4]" /> {t('iko.title')}
        </h3>
        {!editing && (
          <Button variant="outline" size="sm" onClick={startEdit} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-lg" data-testid="iko-edit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> {t('iko.edit')}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('iko.subtitle')}</p>

      {editing ? (
        <div data-testid="iko-edit-panel">
          <IkoCertEditor
            certified={certified}
            certs={certs}
            onChange={({ certified: c, certs: cs }) => { setCertified(c); setCerts(cs); }}
          />
          <div className="flex items-center gap-2 mt-5">
            <Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="iko-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('iko.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving} className="text-muted-foreground hover:text-white" data-testid="iko-cancel-btn">
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : !user?.iko_certified || list.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid="iko-empty">
          <ShieldOff className="w-5 h-5 text-muted-foreground shrink-0" />
          <p className="text-sm text-muted-foreground">{t('iko.none')}</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="iko-list">
          {list.map((c, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-3.5" data-testid={`iko-display-${i}`}>
              <div className="flex items-center gap-2 font-medium">
                <ShieldCheck className="w-4 h-4 text-[#2EE59D] shrink-0" /> {ikoCertName(c.cert)}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                {(c.school || c.location) && (
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />
                    {[c.school, c.location].filter(Boolean).join(' · ')}
                  </span>
                )}
                {when(c) && <span className="flex items-center gap-1.5 font-data"><Calendar className="w-3.5 h-3.5" /> {when(c)}</span>}
                {c.card_number && <span className="flex items-center gap-1.5 font-data"><CreditCard className="w-3.5 h-3.5" /> {c.card_number}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
